import { useState } from 'react'
import { useAcceptDataPolicy } from '../api/rooms'
import type { RoomResponse } from '../api/types'
import { dataPolicyOf } from '../lib/dataPolicy'
import { errorMessage, formatUtc } from '../lib/format'

/**
 * The organization's privacy and data use policy (ADR 0008), shown WHERE THE UPLOAD CONTROL
 * WOULD BE, and above the request list for someone who answers it. Unlike the NDA this does not
 * hide the room: the person can still read what they may read; what they may not do, until they
 * accept, is hand information over. The API enforces that (403 on field `dataPolicy`); this is
 * the way through it, not the lock.
 *
 * Accepting names the version that was read, and the server refuses anything but the current
 * one. One acceptance holds in every room of the organization.
 */
export function DataPolicyGate({ room, doing }: { room: RoomResponse; doing: string }) {
  const accept = useAcceptDataPolicy(room.id)
  const [agreed, setAgreed] = useState(false)
  const policy = dataPolicyOf(room)

  return (
    <section className="panel data-policy-gate" aria-labelledby="data-policy-title">
      <p className="small muted">
        Before {doing}, {room.organizationName} asks you to read and accept its privacy and data use
        policy
        {policy.setUtc ? ` (version ${policy.versionNumber}, ${formatUtc(policy.setUtc)})` : ''}.
        You accept it once for every room of {room.organizationName}.
      </p>
      <h2 id="data-policy-title" className="h2">
        {policy.title}
      </h2>
      <div className="nda-body" tabIndex={0}>
        {policy.body}
      </div>
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault()
          if (agreed && policy.versionId) accept.mutate(policy.versionId)
        }}
      >
        <label className="check">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={accept.isPending}
          />
          <span>I have read this policy and accept it.</span>
        </label>
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!agreed || accept.isPending || !policy.versionId}
          >
            Accept the policy
          </button>
          {accept.isError && <span className="error-text small">{errorMessage(accept.error)}</span>}
        </div>
        <p className="small muted">
          Your acceptance is recorded with the time and the network address it came from.
        </p>
      </form>
    </section>
  )
}
